import { supabase } from './supabase.js';

/** Mensaje cuando la sesión local quedó inválida (refresh fallido o token corrupto). */
export const AUTH_SESSION_STALE_MESSAGE =
  'Tu sesión ha caducado o quedó desincronizada. Vuelve a iniciar sesión si hace falta.';

const NETWORK_ERROR_RE =
  /failed to fetch|networkerror|load failed|err_internet_disconnected|network request failed/i;

/**
 * Errores típicos de refresh / red (GoTrue "Failed to fetch", lock timeout indirecto).
 * @param {unknown} err
 */
export function isAuthNetworkOrRefreshError(err) {
  if (!err) return false;
  const msg = String(err?.message || err?.name || err || '');
  if (NETWORK_ERROR_RE.test(msg)) return true;
  const status = err?.status ?? err?.code;
  if (status === 0 || status === 'ECONNABORTED') return true;
  return false;
}

/** Limpia solo la sesión en este navegador (no revoca refresh en servidor). */
export async function clearStaleLocalAuth() {
  try {
    await supabase.auth.signOut({ scope: 'local' });
  } catch (e) {
    console.warn('[authSessionRecovery] signOut local:', e);
  }
}

/**
 * Revalida sesión tras volver online o enfocar la pestaña.
 * @param {{ hadUserId?: string | null }} opts
 * @returns {Promise<{ user: import('@supabase/supabase-js').User | null, expired: boolean, networkError: boolean }>}
 */
export async function revalidateAuthSession(opts = {}) {
  const hadUserId = opts.hadUserId ?? null;
  let networkError = false;

  try {
    const { data, error } = await supabase.auth.getSession();
    if (error) {
      networkError = isAuthNetworkOrRefreshError(error);
      if (!networkError && hadUserId) {
        await clearStaleLocalAuth();
        return { user: null, expired: true, networkError: false };
      }
      return { user: null, expired: false, networkError };
    }
    const user = data.session?.user ?? null;
    if (hadUserId && !user?.id) {
      await clearStaleLocalAuth();
      return { user: null, expired: true, networkError: false };
    }
    return { user, expired: false, networkError: false };
  } catch (err) {
    networkError = isAuthNetworkOrRefreshError(err);
    if (!networkError && hadUserId) {
      await clearStaleLocalAuth();
      return { user: null, expired: true, networkError: false };
    }
    return { user: null, expired: false, networkError };
  }
}
